import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import ArticleBody from "@/components/blog/ArticleBody";
import { supabase } from "@/integrations/supabase/client";
import { getCoverUrl, type Post } from "@/lib/blog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { ArrowLeft, MessageCircle, Linkedin, Link2 } from "lucide-react";
import logo from "@/assets/logo-abillyting-v2.jpeg.asset.json";
import { useSiteSettings } from "@/hooks/use-site-settings";
import { whatsappLink } from "@/lib/site-settings";

const MAKE_WEBHOOK = "https://hook.eu1.make.com/dzqx93j4uqgypi2fkovojxs1a95wncsv";

const BlogPost = () => {
  const { settings } = useSiteSettings();
  const WHATSAPP_URL = whatsappLink(settings.whatsapp_primary, "Olá! Li um artigo no blog de vocês e quero saber mais sobre os serviços da Abillyting.");
  const { slug } = useParams<{ slug: string }>();
  const { toast } = useToast();
  const [post, setPost] = useState<Post | null>(null);
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [leadEmail, setLeadEmail] = useState("");
  const [leadName, setLeadName] = useState("");
  const [sendingLead, setSendingLead] = useState(false);

  useEffect(() => {
    const load = async () => {
      const { data, error } = await supabase
        .from("posts" as never)
        .select("*")
        .eq("slug", slug ?? "")
        .eq("status", "published")
        .lte("published_at", new Date().toISOString())
        .maybeSingle();
      if (error || !data) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      const p = data as unknown as Post;
      setPost(p);
      setCoverUrl(await getCoverUrl(p.cover_image));
      document.title = p.meta_title || `${p.title} - Blog Abillyting`;
      const desc = p.meta_description || p.excerpt;
      const meta = document.querySelector('meta[name="description"]');
      if (meta && desc) meta.setAttribute("content", desc);
      if (p.keywords?.length) {
        let kw = document.querySelector('meta[name="keywords"]');
        if (!kw) { kw = document.createElement("meta"); kw.setAttribute("name", "keywords"); document.head.appendChild(kw); }
        kw.setAttribute("content", p.keywords.join(", "));
      }
      setLoading(false);
    };
    load();
  }, [slug]);

  useEffect(() => {
    if (!post) return;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.meta_title || post.title,
      description: post.meta_description || post.excerpt || undefined,
      keywords: post.keywords?.length ? post.keywords.join(", ") : undefined,
      datePublished: post.published_at ?? post.created_at,
      dateModified: post.updated_at,
      author: { "@type": "Organization", name: "Abillyting Marketing" },
      publisher: { "@type": "Organization", name: "Abillyting Marketing" },
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [post]);

  const submitLead = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadName.trim() || !leadEmail.trim()) return;
    setSendingLead(true);
    try {
      await fetch(MAKE_WEBHOOK, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: leadName.trim(),
          email: leadEmail.trim(),
          service: "Lead do Blog",
          message: `Lead capturado no artigo: ${post?.title ?? slug}`,
        }),
      });
      toast({ title: "Inscrição confirmada!", description: "Você receberá nossos conteúdos em breve." });
      setLeadName("");
      setLeadEmail("");
    } catch {
      toast({ title: "Erro ao enviar", description: "Tente novamente em instantes.", variant: "destructive" });
    }
    setSendingLead(false);
  };

  if (loading) {
    return (
      <main className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Carregando artigo...</p>
      </main>
    );
  }

  if (notFound || !post) {
    return (
      <main className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <h1 className="font-heading text-3xl font-bold">Artigo não encontrado</h1>
        <Link to="/blog" className="text-primary hover:underline">
          Voltar para o blog
        </Link>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background">
      <header className="border-b border-border bg-background/95 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/">
            <img src={logo.url} alt="Abillyting Marketing" className="h-16 w-auto object-contain" />
          </Link>
          <Link
            to="/blog"
            className="flex items-center gap-2 text-foreground hover:text-primary transition-colors font-medium"
          >
            <ArrowLeft size={18} /> Voltar ao blog
          </Link>
        </div>
      </header>

      <article className="container mx-auto px-4 py-16 max-w-3xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <ArticleBody
            title={post.title}
            category={post.category}
            publishedAt={post.published_at}
            coverUrl={coverUrl}
            content={post.content}
          />

          {/* Share */}
          <div className="mt-12 flex items-center justify-center gap-3 flex-wrap">
            <span className="text-sm text-muted-foreground">Compartilhar:</span>
            <a href={`https://wa.me/?text=${encodeURIComponent(post.title + " " + window.location.href)}`} target="_blank" rel="noopener noreferrer"><Button variant="outline" size="sm" className="gap-1.5"><MessageCircle size={14} /> WhatsApp</Button></a>
            <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`} target="_blank" rel="noopener noreferrer"><Button variant="outline" size="sm" className="gap-1.5"><Linkedin size={14} /> LinkedIn</Button></a>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => { navigator.clipboard.writeText(window.location.href); toast({ title: "Link copiado!" }); }}><Link2 size={14} /> Copiar link</Button>
          </div>

          {/* Lead capture box */}
          <div className="mt-14 bg-card border border-primary/30 rounded-2xl p-8 text-center space-y-4">
            <h2 className="font-heading text-2xl font-bold">
              Gostou do conteúdo? Receba mais estratégias como essa
            </h2>
            <p className="text-muted-foreground">
              Deixe seu e-mail e receba conteúdos exclusivos de marketing e IA para o seu nicho.
            </p>
            <form onSubmit={submitLead} className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
              <Input
                placeholder="Seu nome"
                value={leadName}
                onChange={(e) => setLeadName(e.target.value)}
                required
                maxLength={100}
              />
              <Input
                type="email"
                placeholder="Seu melhor e-mail"
                value={leadEmail}
                onChange={(e) => setLeadEmail(e.target.value)}
                required
                maxLength={255}
              />
              <Button type="submit" disabled={sendingLead} className="whitespace-nowrap">
                {sendingLead ? "Enviando..." : "Quero receber"}
              </Button>
            </form>
          </div>

          {/* WhatsApp CTA */}
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="gap-2 bg-accent hover:bg-accent/90 text-accent-foreground">
                <MessageCircle size={18} /> Falar com um especialista
              </Button>
            </a>
            <Link to="/#contato" className="text-primary hover:underline font-medium">
              Ou solicite um diagnóstico gratuito
            </Link>
          </div>
        </motion.div>
      </article>
    </main>
  );
};

export default BlogPost;
